import {inject, Inject, Injectable, OnDestroy, Optional} from '@angular/core';
import {BehaviorSubject, Observable, of, tap} from 'rxjs';
import {
  AangeverBurgerlijkeStaat, AangeverSamenwoonSituatie,
  Aangifte,
  AangifteBerekening,
  AangifteRapport,
  BOKKZ_BASE_URL,
  BokkzClient,
  Herinvesteringsreserve,
  IncomeTaxEntity,
  JaarrekeningBalans,
  JaarrekeningInvesteringen,
  JaarrekeningVenW,
  MenuInterface,
  OmschrijvingEnBedrag,
  Onderneming,
  ResultaatSamenwerking,
  SpecificatieOnroerendeZaken,
  SpecificatiePriveGebruikAuto,
  SpecificatiePriveGebruikWoning,
  Staking,
} from './api.bokkz.services';
import {Bevinding, ErrorMessage, IbRekenService, UitlegResponse} from './rekenclient.api';
import {FormArray, FormBuilder, FormGroup} from '@angular/forms';
import {HttpClient} from '@angular/common/http';
import {ToastService} from '@snelstart/ui-toast';

export interface Search {
  search: Zoek[];
}

export interface Zoek {
  zoekOpdrachtType: string;
  zoekWaarde: string;
}

export interface AnbiAntwoord {
  fiscaalNummer: number;
  dossierNummer: number;
  naam: string;
  aliasNaam: string;
  vestigingsPlaats: string;
  webSite: string;
  ingangsDatum: string;
  ingangsDatumCultuur: string;
  eindDatum: string;
  intrekkingsDatum: string;
  eindDatumCultuur: string;
  intrekkingsDatumCultuur: string;
}

export interface Kenteken {
  kenteken?: string;
  catalogusprijs?: string;
  datum_tenaamstelling?: string;
  bijtelling?: number;
  bijtellingsPercentage?: number;
  co2_uitstoot_gecombineerd?: number;
}

@Injectable({ providedIn: 'root' })
export class BokkzIbDataService implements OnDestroy {
    private apiService = inject(IbRekenService);
    private toastService = inject(ToastService);
    private bokkzClient = inject(BokkzClient);
    private fb = inject(FormBuilder);
    private http = inject(HttpClient);

    public optimaleAangifte = new BehaviorSubject<Aangifte>(this.getFromSessionStorage<Aangifte>('optimaleAangifte') || ({} as Aangifte));
    public aangifteBerekening = new BehaviorSubject<AangifteBerekening>(
        this.getFromSessionStorage<AangifteBerekening>('aangifteBerekening') || {},
    );
    public partnerAangifteBerekening = new BehaviorSubject<AangifteBerekening>(
        this.getFromSessionStorage<AangifteBerekening>('partnerAangifteBerekening') || {},
    );
    public optimaleBerekening = new BehaviorSubject<AangifteBerekening>(
        this.getFromSessionStorage<AangifteBerekening>('optimaleBerekening') || {},
    );
    public aangifteRapport = new BehaviorSubject<AangifteRapport>(this.getFromSessionStorage<AangifteRapport>('aangifteRapport') || {});
    public partnerAangifteRapport = new BehaviorSubject<AangifteRapport>(
        this.getFromSessionStorage<AangifteRapport>('partnerAangifteRapport') || {},
    );
    public incomeTaxEntity = new BehaviorSubject<IncomeTaxEntity>(
        this.getFromSessionStorage<IncomeTaxEntity>('incomeTaxEntity') || ({} as IncomeTaxEntity),
    );
    public indexOnderneming = new BehaviorSubject<number>(0);
    public controleResponse = new BehaviorSubject<Bevinding[]>([]);
    public errorResponse = new BehaviorSubject<ErrorMessage[]>([]);
    public partnerAangifte = new BehaviorSubject<boolean>(false);
    public aantalValidaties = new BehaviorSubject<number>(0);
    public naamAangever = '';
    public naamPartner = '';
    public uitlegResponse = new BehaviorSubject<UitlegResponse>(
        this.getFromSessionStorage<UitlegResponse>('uitleg') || ({} as UitlegResponse),
    );
    public options = new BehaviorSubject<MenuInterface[]>([]);
    public errorString = '';
    public menuInterface = new BehaviorSubject<MenuInterface[]>([]);
    public isDefAangifte = new BehaviorSubject<boolean>(false);
    public heeftSamenwerkingsverbandWuo = new BehaviorSubject<boolean>(false);
    private apiAnbiUrl = 'https://portaal.belastingdienst.nl/anbi-service/api/search';
    private baseUrl: string;

    public incomeTaxEntity$: Observable<IncomeTaxEntity> = this.incomeTaxEntity.asObservable();
    public aangifteBerekening$: Observable<AangifteBerekening> = this.aangifteBerekening.asObservable();
    public partnerAangifte$: Observable<boolean> = this.partnerAangifte.asObservable();
    public indexOnderneming$: Observable<number> = this.indexOnderneming.asObservable();
    public aantalValidaties$: Observable<number> = this.aantalValidaties.asObservable();
    public errorResponse$: Observable<ErrorMessage[]> = this.errorResponse.asObservable();

    public patchIncomeTaxEntity(updater: (entity: IncomeTaxEntity) => IncomeTaxEntity): void {
        const current = this.incomeTaxEntity.value;
        const updated = updater(current);

        if (updated.aangifte?.box1?.winstUitOnderneming?.ondernemingen) {
            updated.aangifte.box1.winstUitOnderneming.ondernemingen =
                updated.aangifte.box1.winstUitOnderneming.ondernemingen.map((onderneming) => {
                    const { ['fiscaalOndernemingsVermogen']: _, ...ondernemingZonderFov } = onderneming;
                    return {
                        ...ondernemingZonderFov,
                        eigenVermogen: onderneming.eigenVermogen?.filter(
                            (item) => item?.omschrijving !== 'fiscaalOndernemingsVermogen',
                        ),
                    };
                });
        }

        if (updated.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen) {
            updated.aangifte.partnerAangifte.box1.winstUitOnderneming.ondernemingen =
                updated.aangifte.partnerAangifte.box1.winstUitOnderneming.ondernemingen.map((onderneming) => {
                    const { ['fiscaalOndernemingsVermogen']: _, ...ondernemingZonderFov } = onderneming;
                    return {
                        ...ondernemingZonderFov,
                        eigenVermogen: onderneming.eigenVermogen?.filter(
                            (item) => item?.omschrijving !== 'fiscaalOndernemingsVermogen',
                        ),
                    };
                });
        }

        const changed = this.hasChanged(current, updated, (v) => ({
            isDefAangifte: v.taxType,
            gezamenlijkeAangifte: v.aangifte?.gezamenlijkeAangifte,
            burgerlijkeStaat: v.aangifte?.aangever?.burgerlijkeStaat,
            relatiegegevens: v.aangifte?.aangever?.relatiegegevens?.at(0)?.voldaanAanFiscaalPartnerschapVoorwaarden,
            ondernemingen: v.aangifte?.box1?.winstUitOnderneming?.ondernemingen?.length,
            aanmerkelijkBelang: v.aangifte?.box2?.aanmerkelijkBelang,
        }));

        this.incomeTaxEntity.next(updated);
        this.saveToSessionStorage('incomeTaxEntity', updated);

        if (changed) {
            this.createMenuInterface(updated);
        }
    }

    hasChanged<T>(prev: T, curr: T, extract: (val: T) => unknown): boolean {
        return JSON.stringify(extract(prev)) !== JSON.stringify(extract(curr));
    }

    public calculateLatestIncomeTaxReturn(): Observable<AangifteBerekening> {
        const aangifte = this.incomeTaxEntity.value.aangifte;

        if (aangifte) {
            if (aangifte.aangever.burgerlijkeStaat != null && aangifte.aangever.geboortedatum != null && aangifte.aangifteJaar != null) {
                return this.apiService.getIncomeTaxCalculation(aangifte).pipe(
                    tap((result) => {
                        this.aangifteBerekening.next(result);
                    }),
                );
            }
            if (
                aangifte.aangever.burgerlijkeStaat != null &&
                aangifte.aangever.geboortedatum != null &&
                aangifte.partnerAangifte?.aangever?.geboortedatum != null &&
                aangifte.aangifteJaar != null
            ) {
                return this.apiService.getIncomeTaxCalculation(aangifte).pipe(
                    tap((result) => {
                        this.aangifteBerekening.next(result);
                    }),
                );
            } else return of({} as AangifteBerekening);
        } else return of({} as AangifteBerekening);
    }

    public calculateLoonheffingen(): Observable<Aangifte> {
        const aangifte = this.incomeTaxEntity.value.aangifte;

        if (aangifte) {
            return this.apiService.getIncomeTaxLoonheffing(aangifte).pipe(
                tap((updatedAangifte) => {
                    const updatedEntity: IncomeTaxEntity = {
                        ...this.incomeTaxEntity.value,
                        aangifte: updatedAangifte,
                    };
                    this.updateIncomeTaxEntity(updatedEntity);
                }),
            );
        } else return of({} as Aangifte);
    }

    constructor(@Optional() @Inject(BOKKZ_BASE_URL) baseUrl?: string) {
        this.applyDebounceTime();
        this.baseUrl = baseUrl !== undefined && baseUrl !== null ? baseUrl : '';
    }

    private applyDebounceTime() {
        this.optimaleAangifte.pipe(tap((value) => this.saveToSessionStorage('optimaleAangifte', value))).subscribe();
        this.aangifteBerekening.pipe(tap((value) => this.saveToSessionStorage('aangifteBerekening', value))).subscribe();
        this.partnerAangifteBerekening.pipe(tap((value) => this.saveToSessionStorage('partnerAangifteBerekening', value))).subscribe();
        this.optimaleBerekening.pipe(tap((value) => this.saveToSessionStorage('optimaleBerekening', value))).subscribe();
        this.aangifteRapport.pipe(tap((value) => this.saveToSessionStorage('aangifteRapport', value))).subscribe();
        this.partnerAangifteRapport.pipe(tap((value) => this.saveToSessionStorage('partnerAangifteRapport', value))).subscribe();
        this.incomeTaxEntity.pipe(tap((value) => this.saveToSessionStorage('incomeTaxEntity', value))).subscribe();
        this.errorResponse = this.errorResponse as BehaviorSubject<ErrorMessage[]>;
        this.controleResponse = this.controleResponse as BehaviorSubject<Bevinding[]>;
        this.aantalValidaties = this.aantalValidaties as BehaviorSubject<number>;
    }

    //Voor het ophalen uit de sessionstorage.
    private getFromSessionStorage<T>(key: string): T | null {
        const value = sessionStorage.getItem(key);
        return value ? JSON.parse(value) : null;
    }

    //Voor het opslaan in sessionstorage
    private saveToSessionStorage(key: string, value: any) {
        sessionStorage.setItem(key, JSON.stringify(value));
    }

    updateAangifteBerekening(aangifteBerekening: AangifteBerekening) {
        this.aangifteBerekening.next(aangifteBerekening);
        this.saveToSessionStorage('aangifteBerekening', aangifteBerekening);
    }

    updateIncomeTaxEntity(incomeTaxEntity: IncomeTaxEntity) {
        this.incomeTaxEntity.next(incomeTaxEntity);
        this.saveToSessionStorage('incomeTaxEntity', incomeTaxEntity);
    }

    createMenuInterface(incomeTaxEntity: IncomeTaxEntity) {
        const aangifte = incomeTaxEntity.aangifte!;
        const menuInterface: MenuInterface[] = [
            {
                value: 100,
                id: '100',
                label: 'Persoonlijk',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Aangever',
                        pageMapping: 'Aangever',
                        visible: true,
                        value: 101,
                    },
                    {
                        label: 'Wijziging burgerlijke staat',
                        pageMapping: 'Wijziging burgerlijke staat',
                        visible: aangifte?.aangever?.burgerlijkeStaat
                            ? aangifte.aangever.burgerlijkeStaat === AangeverBurgerlijkeStaat.SITUATIE_GEWIJZIGD
                            : false,
                        value: 102,
                    },
                    {
                        label: 'Partner',
                        pageMapping: 'Partner',
                        visible:
                            aangifte?.aangever?.burgerlijkeStaat === AangeverBurgerlijkeStaat.GEHUWD ||
                            (aangifte?.aangever?.burgerlijkeStaat === AangeverBurgerlijkeStaat.SAMENWONEND &&
                                aangifte?.aangever?.samenwoonSituatie !== AangeverSamenwoonSituatie.GEEN) ||
                            (aangifte?.aangever?.burgerlijkeStaat === AangeverBurgerlijkeStaat.SITUATIE_GEWIJZIGD &&
                                aangifte.aangever?.relatiegegevens?.at(0)?.voldaanAanFiscaalPartnerschapVoorwaarden === true),
                        value: 103,
                    },
                    {
                        label: 'Kinderen',
                        pageMapping: 'Kinderen',
                        visible: true,
                        value: 104,
                    },
                    {
                        label: 'Inlezen',
                        pageMapping: 'Inlezen',
                        visible: incomeTaxEntity.taxType === 'DA',
                        value: 105,
                    },
                ],
            },
            {
                value: 200,
                id: '200',
                label: 'Winst uit onderneming',
                visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                subonderdelen: [
                    {
                        label: 'Ondernemingen',
                        pageMapping: 'Ondernemingen',
                        visible: true,
                        value: 210,
                    },
                    {
                        label: 'Jaarstukken',
                        pageMapping: 'Jaarstukken',
                        visible: false,
                        value: 211,
                    },
                    {
                        label: 'Ondernemersaftrek',
                        pageMapping: 'Ondernemersaftrek',
                        visible: true,
                        value: 221,
                    },
                    {
                        label: 'Samenvatting',
                        pageMapping: 'Samenvatting winst',
                        visible: true,
                        value: 222,
                    },
                ],
            },
            {
                value: 300,
                id: '300',
                label: 'Loon & uitkeringen',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Inkomsten uit loondienst',
                        pageMapping: 'Inkomsten uit loondienst',
                        visible: true,
                        value: 310,
                    },
                    {
                        label: 'Reiskosten openbaar vervoer',
                        pageMapping: 'Reiskosten openbaar vervoer',
                        visible:
                            this.isDefAangifte.value &&
                            (aangifte?.box1?.belastbaarLoon?.loonUitTegenwoordigeDienstbetrekking !== undefined ||
                                aangifte?.partnerAangifte?.box1?.belastbaarLoon?.loonUitTegenwoordigeDienstbetrekking !== undefined) &&
                            aangifte?.biljetSoort
                                ? aangifte.biljetSoort !== 'C'
                                : false,
                        value: 320,
                    },
                    {
                        label: 'Pensioen en andere uitkeringen',
                        pageMapping: 'Pensioen en andere uitkeringen',
                        visible: true,
                        value: 330,
                    },
                    {
                        label: 'Meer banen of uitkeringen tegelijk',
                        pageMapping: 'Meer banen of uitkeringen tegelijk',
                        visible: incomeTaxEntity.taxType === 'VA',
                        value: 340,
                    },
                    {
                        label: 'Buitenlands loon/pensioen',
                        pageMapping: 'Buitenlands loon',
                        visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                        value: 350,
                    },
                    {
                        label: 'Vrijgesteld loon',
                        pageMapping: 'Vrijgesteld loon',
                        visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                        value: 360,
                    },
                    {
                        label: 'Samenvatting',
                        pageMapping: 'Samenvatting loon',
                        visible: true,
                        value: 370,
                    },
                ],
            },
            {
                value: 400,
                id: '400',
                label: 'Overige inkomsten',
                visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                subonderdelen: [
                    {
                        label: 'Resultaat overige werkzaamheden',
                        pageMapping: 'Resultaat uit overige werkzaamheden',
                        visible: true,
                        value: 410,
                    },
                    {
                        label: 'Terbeschikkingstellingsregeling',
                        pageMapping: 'Terbeschikkingstellingsregeling',
                        visible: true,
                        value: 420,
                    },
                    {
                        label: 'Alimentatie',
                        pageMapping: 'Alimentatie',
                        visible: true,
                        value: 430,
                    },
                    {
                        label: 'Periodieke uitkeringen',
                        pageMapping: 'Periodieke uitkeringen',
                        visible: true,
                        value: 440,
                    },
                    {
                        label: 'Samenvatting',
                        pageMapping: 'Samenvatting overige inkomsten',
                        visible: true,
                        value: 450,
                    },
                ],
            },
            {
                value: 500,
                id: '500',
                label: 'Eigen woning',
                visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                subonderdelen: [
                    {
                        label: 'Woningen',
                        pageMapping: 'Woningen',
                        visible: true,
                        value: 510,
                    },
                    {
                        label: 'Eigenwoningschulden',
                        pageMapping: 'Eigenwoningschulden',
                        visible: true,
                        value: 520,
                    },
                    {
                        label: 'Financieringskosten',
                        pageMapping: 'Financieringskosten',
                        visible: true,
                        value: 530,
                    },
                    {
                        label: 'Restschulden',
                        pageMapping: 'Restschulden',
                        visible: incomeTaxEntity.taxType === 'DA',
                        value: 540,
                    },
                    {
                        label: 'Huurinkomsten',
                        pageMapping: 'Huurinkomsten',
                        visible: incomeTaxEntity.taxType === 'DA',
                        value: 550,
                    },
                    {
                        label: 'Overig eigen woning',
                        pageMapping: 'Overig eigen woning',
                        visible: incomeTaxEntity.taxType === 'DA',
                        value: 560,
                    },
                    {
                        label: 'Samenvatting',
                        pageMapping: 'Samenvatting eigen woning',
                        visible: true,
                        value: 570,
                    },
                ],
            },
            {
                value: 600,
                id: '600',
                label: 'Premies inkomensvoorzieningen',
                visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                subonderdelen: [
                    {
                        label: 'Premies lijfrente',
                        pageMapping: 'Premies lijfrente',
                        visible: true,
                        value: 610,
                    },
                    {
                        label: 'Jaar- en reserveringsruimte',
                        pageMapping: 'Jaar- en reserveringsruimte',
                        visible: true,
                        value: 620,
                    },
                    {
                        label: 'Overige premies inkomensvoorzieningen',
                        pageMapping: 'Overige premies inkomensvoorzieningen',
                        visible: true,
                        value: 630,
                    },
                    {
                        label: 'Teruggave premies inkomensvoorzieningen',
                        pageMapping: 'Teruggave premies inkomensvoorzieningen',
                        visible: true,
                        value: 640,
                    },
                    {
                        label: 'Samenvatting',
                        pageMapping: 'Samenvatting premies inkomensvoorzieningen',
                        visible: true,
                        value: 650,
                    },
                ],
            },
            {
                value: 700,
                id: '700',
                label: 'Aanmerkelijk belang (box 2)',
                visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                subonderdelen: [
                    {
                        label: 'Inkomen uit aanmerkelijk belang',
                        pageMapping: 'Aanmerkelijk belang (box 2)',
                        visible: true,
                        value: 710,
                    },
                    {
                        label: 'Samenvatting',
                        pageMapping: 'Samenvatting aanmerkelijk belang',
                        visible: true,
                        value: 720,
                    },
                ],
            },
            {
                value: 800,
                id: '800',
                label: 'Vermogen (box 3)',
                visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                subonderdelen: [
                    {
                        label: 'Werkelijk rendement',
                        pageMapping: 'Werkelijk rendement',
                        visible: aangifte?.aangifteJaar ? aangifte.aangifteJaar >= 2025 : false,
                        value: 810,
                    },
                    {
                        label: 'Bankrekeningen',
                        pageMapping: 'Bankrekeningen',
                        visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                        value: 820,
                    },
                    {
                        label: 'Beleggingen',
                        pageMapping: 'Beleggingen',
                        visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                        value: 830,
                    },
                    {
                        label: 'Overige onroerende zaken',
                        pageMapping: 'Overige onroerende zaken',
                        visible: true,
                        value: 840,
                    },
                    {
                        label: 'Overige bezittingen',
                        pageMapping: 'Overige bezittingen',
                        visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                        value: 850,
                    },
                    {
                        label: 'Schulden',
                        pageMapping: 'Schulden',
                        visible: true,
                        value: 860,
                    },
                    {
                        label: 'Samenvatting',
                        pageMapping: 'Samenvatting box 3',
                        visible: true,
                        value: 870,
                    },
                ],
            },
            {
                value: 900,
                id: '900',
                label: 'Persoonsgebonden aftrek',
                visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                subonderdelen: [
                    {
                        label: 'Specifieke zorgkosten',
                        pageMapping: 'Specifieke zorgkosten',
                        visible: true,
                        value: 910,
                    },
                    {
                        label: 'Giften',
                        pageMapping: 'Giften',
                        visible: true,
                        value: 920,
                    },
                    {
                        label: 'Scholingsuitgaven',
                        pageMapping: 'Scholingsuitgaven',
                        visible: true,
                        value: 930,
                    },
                    {
                        label: 'Onderhoudsverplichtingen',
                        pageMapping: 'Onderhoudsverplichtingen',
                        visible: true,
                        value: 940,
                    },
                    {
                        label: 'Weekenduitgaven gehandicapte kinderen',
                        pageMapping: 'Weekenduitgaven gehandicapte kinderen',
                        visible: true,
                        value: 950,
                    },
                    {
                        label: 'Restant pga voorgaande jaren',
                        pageMapping: 'Restant persoonsgebonden aftrek voorgaande jaren',
                        visible: true,
                        value: 960,
                    },
                    {
                        label: 'Negatieve pga',
                        pageMapping: 'Negatieve persoonsgebonden aftrek',
                        visible: true,
                        value: 970,
                    },
                    {
                        label: 'Samenvatting',
                        pageMapping: 'Samenvatting persoonsgebonden aftrek',
                        visible: true,
                        value: 980,
                    },
                ],
            },
            {
                value: 1000,
                id: '1000',
                label: 'Bijzondere situaties',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Premieplicht',
                        pageMapping: 'Premieplicht',
                        visible: true,
                        value: 1010,
                    },
                    {
                        label: 'Wereldinkomen',
                        pageMapping: 'Wereldinkomen',
                        visible: aangifte?.buitenlandseBelastingplicht?.inwonerLandNonDiscriminatie || false,
                        value: 1020,
                    },
                ],
            },
            {
                value: 1100,
                id: '1100',
                label: 'Voorheffingen & verliezen',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Voorlopige aanslagen ib / zvw',
                        pageMapping: 'Voorlopige aanslagen',
                        visible: true,
                        value: 1110,
                    },
                    {
                        label: 'Nederlandse dividendbelasting',
                        pageMapping: 'Nederlandse dividendbelasting',
                        visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                        value: 1120,
                    },
                    {
                        label: 'Buitenlandse bronbelasting',
                        pageMapping: 'Buitenlandse bronbelasting',
                        visible: aangifte?.biljetSoort ? aangifte.biljetSoort !== 'C' : false,
                        value: 1130,
                    },
                    {
                        label: 'Loon als onderdeel van winst',
                        pageMapping: 'Loon als onderdeel van winst',
                        visible: incomeTaxEntity.taxType === 'DA',
                        value: 1140,
                    },
                    {
                        label: 'Nog verrekenbaar',
                        pageMapping: 'Nog verrekenbaar',
                        visible: aangifte?.biljetSoort
                            ? aangifte.biljetSoort !== 'C' && incomeTaxEntity.taxType === 'DA'
                            : incomeTaxEntity.taxType === 'DA',
                        value: 1150,
                    },
                ],
            },
            {
                value: 1200,
                id: '1200',
                label: aangifte?.gezamenlijkeAangifte ? 'Verdeling & berekeningen' : 'Berekeningen',
                visible: true,
                subonderdelen: [
                    {
                        label: 'Verdeling',
                        pageMapping: 'Verdeling',
                        visible: aangifte?.gezamenlijkeAangifte ?? false,
                        value: 1210,
                    },
                    {
                        label: 'Inkomen box 1',
                        pageMapping: 'Overzicht inkomen box 1',
                        visible: true,
                        value: 1220,
                    },
                    {
                        label: 'Inkomen box 2',
                        pageMapping: 'Overzicht inkomen box 2',
                        visible: aangifte?.box2 !== undefined,
                        value: 1230,
                    },
                    {
                        label: 'Inkomen box 3',
                        pageMapping: 'Overzicht inkomen box 3',
                        visible: aangifte?.box3 !== undefined || aangifte?.verdeling?.aandeelRendementsGrondslag !== undefined,
                        value: 1240,
                    },
                    {
                        label: 'Berekening inkomstenbelasting',
                        pageMapping: 'Berekening inkomstenbelasting',
                        visible: true,
                        value: 1250,
                    },
                    {
                        label: 'Berekening zorgverzekeringswet',
                        pageMapping: 'Berekening zorgverzekeringswet',
                        visible: true,
                        value: 1260,
                    },
                ],
            },
        ];
        this.menuInterface.next(menuInterface);
    }

    public saveIncomeTaxEntity(partner: boolean, incomeTaxEntity: IncomeTaxEntity) {
        this.bokkzClient.postIncomeTax(incomeTaxEntity.sstRelationId!, false, partner, false, false, incomeTaxEntity).subscribe({
            next: (response) => {
                if (response && response.aangifte != null) {
                    this.updateIncomeTaxEntity(response);
                    this.getValidation(response);
                }
            },
            error: (err) => {
                console.log(err);
                this.toastService.showErrorMessage(
                    'De gegevens zijn niet opgeslagen. De pagina wordt automatisch vernieuwd en probeer het daarna opnieuw.',
                );
                setTimeout(() => window.location.reload(), 3000);
            },
        });
    }

    public getValidation(entity: IncomeTaxEntity) {
        if (entity.aangifte !== undefined && entity.taxType !== undefined) {
            this.apiService.getIncomeTaxValidation(entity.aangifte, entity.taxType?.includes('VA')).subscribe({
                next: (response) => {
                    if (response.verzamelinkomen != null) {
                        this.aantalValidaties.next(0);
                    } else {
                        if ('HV000028: Unexpected exception during isValid call.' === (response as unknown as string)) {
                            this.errorString = response as unknown as string;
                            const error: ErrorMessage[] = [
                                {
                                    error: 'server.error',
                                    response: 'HV000028: Unexpected exception during isValid call.',
                                },
                            ];
                            this.errorResponse.next(error);
                            this.aantalValidaties.next(1);
                        } else {
                            this.errorResponse.next(response as ErrorMessage[]);
                            this.aantalValidaties.next(this.errorResponse.getValue().length);
                        }
                    }
                },
                error: (err) => {
                    this.aantalValidaties.next(0);
                },
                // complete: () => console.log("complete")
            });
        }
    }

    public createOndernemingFormGroup(onderneming?: Onderneming): FormGroup {
        return this.fb.group({
            werkzaamheden: onderneming?.werkzaamheden || undefined,
            naam: onderneming?.naam || undefined,
            administratieId: onderneming?.administratieId || undefined,
            ondernemingsvorm: onderneming?.ondernemingsvorm || undefined,
            rsin: onderneming?.rsin || undefined,
            landCode: onderneming?.landCode || undefined,
            invullenOpNiveauSamenwerkingsverband: [onderneming?.invullenOpNiveauSamenwerkingsverband === true ? true : undefined],
            invullenOpPersoonlijkNiveau: [onderneming?.invullenOpPersoonlijkNiveau === true],
            invullenOpJaaropgaveCV: onderneming?.invullenOpJaaropgaveCV != null ? onderneming.invullenOpJaaropgaveCV : false,
            zeescheepvaart: onderneming?.zeescheepvaart || undefined,
            startOnderneming: onderneming?.startOnderneming || (null as unknown as Date),
            datumStaking: onderneming?.datumStaking || (null as unknown as Date),
            staking: onderneming?.staking !== null ? this.createStakingFormGroup(onderneming?.staking) : undefined,
            medegerechtigdeOfSchuldeiser:
                onderneming?.medegerechtigdeOfSchuldeiser != null ? onderneming.medegerechtigdeOfSchuldeiser : undefined,
            winstUitHoofdeVanSchuldvordering:
                onderneming?.winstUitHoofdeVanSchuldvordering != null ? onderneming.winstUitHoofdeVanSchuldvordering : undefined,
            agrarischeActiviteiten: onderneming?.agrarischeActiviteiten != null ? onderneming.agrarischeActiviteiten : undefined,
            // specificatieAgrarischeActiviteiten: onderneming?.specificatieAgrarischeActiviteiten != null ? this.createSpecAgrActFG(onderneming?.specificatieAgrarischeActiviteiten) : this.fb.array([]),
            waarderingsstelselGewijzigd:
                onderneming?.waarderingsstelselGewijzigd != null ? onderneming.waarderingsstelselGewijzigd : undefined,
            willekeurigAfgeschreven: onderneming?.willekeurigAfgeschreven != null ? onderneming.willekeurigAfgeschreven : undefined,
            schadeloosstellingBuitenWinstOm:
                onderneming?.schadeloosstellingBuitenWinstOm != null ? onderneming.schadeloosstellingBuitenWinstOm : undefined,
            schadeloosstellingBuitenWinst: this.fb.array<OmschrijvingEnBedrag>(onderneming?.schadeloosstellingBuitenWinst || []),

            wuoOpbrengsten: this.fb.array<JaarrekeningVenW>(onderneming?.wuoOpbrengsten || []),
            wuoVrijgesteldeWinstbestanddelen:
                onderneming?.wuoVrijgesteldeWinstbestanddelen != null
                    ? this.createVrijgesteldeWinstbestanddelenFG(onderneming?.wuoVrijgesteldeWinstbestanddelen)
                    : this.fb.array([]),
            wuoStakingswinsten:
                onderneming?.datumStaking != null ? this.createWuoStakingsWinstenFormgroup(onderneming?.wuoStakingswinsten) : undefined,
            financieleBaten: this.fb.array<JaarrekeningVenW>(onderneming?.financieleBaten || []),
            buitengewoneBaten: this.fb.array<JaarrekeningVenW>(onderneming?.buitengewoneBaten || []),
            wuoKosten: this.fb.array<JaarrekeningVenW>(onderneming?.wuoKosten || []),
            financieleLasten: this.fb.array<JaarrekeningVenW>(onderneming?.financieleLasten || []),
            buitengewoneLasten: this.fb.array<JaarrekeningVenW>(onderneming?.buitengewoneLasten || []),
            immaterieleVasteActiva: this.fb.array<JaarrekeningBalans>(onderneming?.immaterieleVasteActiva || []),
            materieleVasteActiva: this.fb.array<JaarrekeningBalans>(onderneming?.materieleVasteActiva || []),
            financieleVasteActiva: this.fb.array<JaarrekeningBalans>(onderneming?.financieleVasteActiva || []),
            vlottendeActivaVoorraad: this.fb.array<JaarrekeningBalans>(onderneming?.vlottendeActivaVoorraad || []),
            vlottendeActivaVorderingen: this.fb.array<JaarrekeningBalans>(onderneming?.vlottendeActivaVorderingen || []),
            effecten: this.fb.array<JaarrekeningBalans>(onderneming?.effecten || []),
            liquideMiddelen: this.fb.array<JaarrekeningBalans>(onderneming?.liquideMiddelen || []),
            eigenVermogen: this.fb.array<JaarrekeningBalans>(onderneming?.eigenVermogen || []),
            voorzieningen: this.fb.array<JaarrekeningBalans>(onderneming?.voorzieningen || []),
            specificatieHerinvesteringsreserve:
                onderneming?.specificatieHerinvesteringsreserve != null
                    ? this.createSpecHIRFG(onderneming?.specificatieHerinvesteringsreserve)
                    : this.fb.array([]),
            langlopendeSchulden: this.fb.array<JaarrekeningBalans>(onderneming?.langlopendeSchulden || []),
            kortlopendeSchulden: this.fb.array<JaarrekeningBalans>(onderneming?.kortlopendeSchulden || []),
            investeringen: onderneming?.investeringen != null ? this.createInvesteringenFG(onderneming?.investeringen) : this.fb.array([]),
            restantInvesteringsaftrek:
                onderneming?.restantInvesteringsaftrek != null
                    ? this.createRestantInvesteringsaftrekFG(onderneming?.restantInvesteringsaftrek)
                    : this.fb.array([]),
            onttrekkingOG: onderneming?.onttrekkingOG != null ? this.createOnttrekkingOGFG(onderneming?.onttrekkingOG) : this.fb.array([]),
            onttrekkingPriveGebruikAuto:
                onderneming?.onttrekkingPriveGebruikAuto != null
                    ? this.createOnttrekkingPriveGebruikAutoFG(onderneming?.onttrekkingPriveGebruikAuto)
                    : this.fb.array([]),
            onttrekkingPriveGebruikWoning:
                onderneming?.onttrekkingPriveGebruikWoning != null
                    ? this.createOnttrekkingPriveGebruikWoningFG(onderneming?.onttrekkingPriveGebruikWoning)
                    : this.fb.array([]),
            onttrekkingenOverig:
                onderneming?.onttrekkingenOverig != null
                    ? this.createOnttrekkingOverigFG(onderneming?.onttrekkingenOverig)
                    : this.fb.array([]),
            stortingOG: onderneming?.stortingOG != null ? this.createStortingOGFG(onderneming?.stortingOG) : this.fb.array([]),
            stortingenOverig:
                onderneming?.stortingenOverig != null ? this.createStortingenOverigFG(onderneming?.stortingenOverig) : this.fb.array([]),
            nietAftrekbareKosten:
                onderneming?.nietAftrekbareKosten != null
                    ? this.createNietAftrekbareKostenFG(onderneming?.nietAftrekbareKosten)
                    : this.fb.array([]),
            beperktAftrekbareKosten:
                onderneming?.beperktAftrekbareKosten != null
                    ? this.createBeperktAftrekbareKostenFG(onderneming?.beperktAftrekbareKosten)
                    : this.fb.array([]),

            beginstandFOR: onderneming?.beginstandFOR || undefined,
            dotatieFOR: onderneming?.dotatieFOR || undefined,
            vrijvalFOR: onderneming?.vrijvalFOR || undefined,
            beginstandHIR: onderneming?.beginstandHIR != null ? this.createBeginstandHIRFG(onderneming?.beginstandHIR) : this.fb.array([]),
            dotatieHIR: onderneming?.dotatieHIR || undefined,
            vrijvalHIR: onderneming?.vrijvalHIR || undefined,
            beginstandEgalisatiereserve:
                onderneming?.beginstandHIR != null
                    ? this.createBeginstandKERFG(onderneming?.beginstandEgalisatiereserve)
                    : this.fb.array([]),
            dotatieEgalisatiereserve: onderneming?.dotatieEgalisatiereserve || undefined,
            vrijvalEgalisatiereserve: onderneming?.vrijvalEgalisatiereserve || undefined,
            resultaatSamenwerking: this.createResultaatSamenwerkingFormGroup(onderneming?.resultaatSamenwerking),
            maximumVerliesBepaling: onderneming?.maximumVerliesBepaling || undefined,
        });
    }

    private createStakingFormGroup(staking?: Staking) {
        return this.fb.group({
            stakingDoorOverdrachtOnderneming: staking?.stakingDoorOverdrachtOnderneming ?? false,
            overdrachtOnroerendeZakenNaarPrive: staking?.overdrachtOnroerendeZakenNaarPrive ?? false,
            overdrachtOverigeBestanddelenNaarPrive: staking?.overdrachtOverigeBestanddelenNaarPrive ?? false,
            overdrachtNaarBV: staking?.overdrachtNaarBV ?? false,
            doorschuivenGeruisloosNaarMedeOndernemer: staking?.doorschuivenGeruisloosNaarMedeOndernemer ?? false,
            doorschuivenGeruisloosDoorOverlijden: staking?.doorschuivenGeruisloosDoorOverlijden ?? false,
            doorschuivenGeruisloosInbrengInBV: staking?.doorschuivenGeruisloosInbrengInBV ?? false,
        });
    }

    public createOnttrekkingPriveGebruikAutoFG(onttrekkingPriveGebruikAuto?: SpecificatiePriveGebruikAuto[]) {
        const ondernemingFG = this.fb.group({
            onttrekkingPriveGebruikAuto: this.fb.array([]),
        });

        const array = ondernemingFG.get('onttrekkingPriveGebruikAuto') as FormArray;
        if (onttrekkingPriveGebruikAuto != undefined) {
            for (let i = 0; i < onttrekkingPriveGebruikAuto?.length; i++) {
                const group = this.fb.group<SpecificatiePriveGebruikAuto>({
                    autoWordtPriveGebruikt: onttrekkingPriveGebruikAuto[i].autoWordtPriveGebruikt || (undefined as unknown as boolean),
                    kenteken: onttrekkingPriveGebruikAuto[i].kenteken || (undefined as unknown as string),
                    catalogusPrijs: onttrekkingPriveGebruikAuto[i].catalogusPrijs || (undefined as unknown as number),
                    periodePriveGebruik: this.fb.group({
                        begindatum: onttrekkingPriveGebruikAuto[i].periodePriveGebruik?.begindatum || (undefined as unknown as Date),
                        einddatum: onttrekkingPriveGebruikAuto[i].periodePriveGebruik?.begindatum || (undefined as unknown as Date),
                    }),
                    kilometerAdministratieAanwezig:
                        onttrekkingPriveGebruikAuto[i].kilometerAdministratieAanwezig || (undefined as unknown as boolean),
                    kostenEnLastenVoorEigenRekening:
                        onttrekkingPriveGebruikAuto[i].kostenEnLastenVoorEigenRekening || (undefined as unknown as number),
                    priveGebruik: onttrekkingPriveGebruikAuto[i].priveGebruik || (undefined as unknown as number),
                });
                array.push(group);
            }
        }
        return array;
    }

    private createOnttrekkingOverigFG(onttrekkingOverig?: OmschrijvingEnBedrag[]) {
        const ondernemingFG = this.fb.group({
            onttrekkingOverig: this.fb.array([]),
        });

        const array = ondernemingFG.get('onttrekkingOverig') as FormArray;
        if (onttrekkingOverig != undefined) {
            for (let i = 0; i < onttrekkingOverig?.length; i++) {
                const group = this.fb.group<OmschrijvingEnBedrag>({
                    omschrijving: onttrekkingOverig[i].omschrijving || (undefined as unknown as string),
                    bedrag: onttrekkingOverig[i].bedrag || (undefined as unknown as number),
                });
                array.push(group);
            }
        }
        return array;
    }

    private createOnttrekkingOGFG(onttrekkingOG?: SpecificatieOnroerendeZaken[]) {
        const ondernemingFG = this.fb.group({
            onttrekkingOG: this.fb.array([]),
        });

        const array = ondernemingFG.get('onttrekkingOG') as FormArray;
        if (onttrekkingOG != undefined) {
            for (let i = 0; i < onttrekkingOG?.length; i++) {
                const group = this.fb.group<SpecificatieOnroerendeZaken>({
                    adresOnroerendeZaak: this.fb.group({
                        straat: onttrekkingOG[i].adresOnroerendeZaak?.straat || (undefined as unknown as string),
                        huisNummer: onttrekkingOG[i].adresOnroerendeZaak?.huisNummer || (undefined as unknown as number),
                        huisNummerToevoeging:
                            onttrekkingOG[i].adresOnroerendeZaak?.huisNummerToevoeging || (undefined as unknown as string),
                        postcode: onttrekkingOG[i].adresOnroerendeZaak?.postcode || (undefined as unknown as string),
                        woonplaats: onttrekkingOG[i].adresOnroerendeZaak?.woonplaats || (undefined as unknown as string),
                        landCode: onttrekkingOG[i].adresOnroerendeZaak?.landCode || (undefined as unknown as string),
                    }),
                    waardeEconomischeVerkeer: onttrekkingOG[i].waardeEconomischeVerkeer || (undefined as unknown as number),
                    boekwaardeOpMonentOnttrekking: onttrekkingOG[i].boekwaardeOpMonentOnttrekking || (undefined as unknown as number),
                    transactieMetDerde: onttrekkingOG[i].transactieMetDerde || (undefined as unknown as boolean),
                    taxateurBetrokken: onttrekkingOG[i].taxateurBetrokken || (undefined as unknown as boolean),
                    naamTaxateur: onttrekkingOG[i].naamTaxateur || (undefined as unknown as string),
                });
                array.push(group);
            }
        }
        return array;
    }

    private createOnttrekkingPriveGebruikWoningFG(onttrekkingPriveGebruikWoning?: SpecificatiePriveGebruikWoning[]) {
        const ondernemingFG = this.fb.group({
            onttrekkingPriveGebruikWoning: this.fb.array([]),
        });

        const array = ondernemingFG.get('onttrekkingPriveGebruikWoning') as FormArray;
        if (onttrekkingPriveGebruikWoning != undefined) {
            for (let i = 0; i < onttrekkingPriveGebruikWoning?.length; i++) {
                const group = this.fb.group<SpecificatiePriveGebruikWoning>({
                    adresWoning: this.fb.group({
                        straat: onttrekkingPriveGebruikWoning[i].adresWoning?.straat || (undefined as unknown as string),
                        huisNummer: onttrekkingPriveGebruikWoning[i].adresWoning?.huisNummer || (undefined as unknown as number),
                        huisNummerToevoeging:
                            onttrekkingPriveGebruikWoning[i].adresWoning?.huisNummerToevoeging || (undefined as unknown as string),
                        postcode: onttrekkingPriveGebruikWoning[i].adresWoning?.postcode || (undefined as unknown as string),
                        woonplaats: onttrekkingPriveGebruikWoning[i].adresWoning?.woonplaats || (undefined as unknown as string),
                        landCode: onttrekkingPriveGebruikWoning[i].adresWoning?.landCode || (undefined as unknown as string),
                    }),
                    wozWaarde: onttrekkingPriveGebruikWoning[i].wozWaarde || (undefined as unknown as number),
                    periodeBewoning: this.fb.group({
                        begindatum: onttrekkingPriveGebruikWoning[i].periodeBewoning?.begindatum || (undefined as unknown as Date),
                        einddatum: onttrekkingPriveGebruikWoning[i].periodeBewoning?.begindatum || (undefined as unknown as Date),
                    }),
                    percentageGerechtigd: onttrekkingPriveGebruikWoning[i].percentageGerechtigd || (undefined as unknown as number),
                    berekendeOnttrekking: onttrekkingPriveGebruikWoning[i].berekendeOnttrekking || (undefined as unknown as number),
                });
                array.push(group);
            }
        }
        return array;
    }

    private createStortingenOverigFG(stortingenOverig?: OmschrijvingEnBedrag[]) {
        const ondernemingFG = this.fb.group({
            onttrekkingOverig: this.fb.array([]),
        });

        const array = ondernemingFG.get('onttrekkingOverig') as FormArray;
        if (stortingenOverig != undefined) {
            for (let i = 0; i < stortingenOverig?.length; i++) {
                const group = this.fb.group<OmschrijvingEnBedrag>({
                    omschrijving: stortingenOverig[i] ? stortingenOverig[i].omschrijving : (undefined as unknown as string),
                    bedrag: stortingenOverig[i] ? stortingenOverig[i].bedrag : (undefined as unknown as number),
                });
                array.push(group);
            }
        }
        return array;
    }

    private createStortingOGFG(stortingOG?: SpecificatieOnroerendeZaken[]) {
        const ondernemingFG = this.fb.group({
            onttrekkingOG: this.fb.array([]),
        });

        const array = ondernemingFG.get('onttrekkingOG') as FormArray;
        if (stortingOG != undefined) {
            for (let i = 0; i < stortingOG?.length; i++) {
                const group = this.fb.group<SpecificatieOnroerendeZaken>({
                    adresOnroerendeZaak: this.fb.group({
                        straat: stortingOG[i].adresOnroerendeZaak?.straat || (undefined as unknown as string),
                        huisNummer: stortingOG[i].adresOnroerendeZaak?.huisNummer || (undefined as unknown as number),
                        huisNummerToevoeging: stortingOG[i].adresOnroerendeZaak?.huisNummerToevoeging || (undefined as unknown as string),
                        postcode: stortingOG[i].adresOnroerendeZaak?.postcode || (undefined as unknown as string),
                        woonplaats: stortingOG[i].adresOnroerendeZaak?.woonplaats || (undefined as unknown as string),
                        landCode: stortingOG[i].adresOnroerendeZaak?.landCode || (undefined as unknown as string),
                    }),
                    waardeEconomischeVerkeer: stortingOG[i].waardeEconomischeVerkeer || (undefined as unknown as number),
                    taxateurBetrokken: stortingOG[i].taxateurBetrokken || (undefined as unknown as boolean),
                    naamTaxateur: stortingOG[i].naamTaxateur || (undefined as unknown as string),
                });
                array.push(group);
            }
        }
        return array;
    }

    private createInvesteringenFG(investeringen?: JaarrekeningInvesteringen[]) {
        const ondernemingFG = this.fb.group({
            investeringen: this.fb.array([]),
        });

        const array = ondernemingFG.get('investeringen') as FormArray;
        if (investeringen != undefined) {
            for (let i = 0; i < investeringen?.length; i++) {
                const group = this.fb.group<JaarrekeningInvesteringen>({
                    omschrijving: investeringen[i]?.omschrijving || '',
                    datumIngebruikname: investeringen[i] ? investeringen[i].datumIngebruikname : undefined,
                    aandeelInInvesteringsBedrag: investeringen[i] ? investeringen[i].aandeelInInvesteringsBedrag : undefined,
                    claimEIA: investeringen[i] ? investeringen[i].claimEIA : undefined,
                    investeringsbedragEIA: investeringen[i] ? investeringen[i].investeringsbedragEIA : undefined,
                    meldingsNummerEia: investeringen[i] ? investeringen[i].meldingsNummerEia : undefined,
                    claimMIA: investeringen[i] ? investeringen[i].claimMIA : undefined,
                    investeringsbedragMIA: investeringen[i] ? investeringen[i].investeringsbedragMIA : undefined,
                    meldingsNummerMia: investeringen[i] ? investeringen[i].meldingsNummerMia : undefined,
                    miaLetter: investeringen[i] ? investeringen[i].miaLetter : undefined,
                    claimKIA: investeringen[i] ? investeringen[i].claimKIA : undefined,
                    investeringsbedragKIA: investeringen[i] ? investeringen[i].investeringsbedragKIA : undefined,
                    reedsBetaaldDoorAangever: investeringen[i] ? investeringen[i].reedsBetaaldDoorAangever : undefined,
                    grondslagDesinvestering: investeringen[i] ? investeringen[i].grondslagDesinvestering : undefined,
                    percentageInvesteringsaftrek: investeringen[i] ? investeringen[i].percentageInvesteringsaftrek : undefined,
                });
                array.push(group);
            }
        }
        return array;
    }

    private createNietAftrekbareKostenFG(nietAftrekbareKosten: OmschrijvingEnBedrag[] | undefined) {
        const ondernemingFG = this.fb.group({
            nietAftrekbareKosten: this.fb.array([]),
        });

        const array = ondernemingFG.get('nietAftrekbareKosten') as FormArray;
        if (nietAftrekbareKosten != undefined) {
            for (let i = 0; i < nietAftrekbareKosten?.length; i++) {
                const group = this.fb.group<OmschrijvingEnBedrag>({
                    omschrijving: nietAftrekbareKosten[i] ? nietAftrekbareKosten[i].omschrijving : undefined,
                    bedrag: nietAftrekbareKosten[i] ? nietAftrekbareKosten[i].bedrag : undefined,
                });
                array.push(group);
            }
        }
        return array;
    }

    private createBeperktAftrekbareKostenFG(beperktAftrekbareKosten: OmschrijvingEnBedrag[] | undefined) {
        const ondernemingFG = this.fb.group({
            nietAftrekbareKosten: this.fb.array([]),
        });

        const array = ondernemingFG.get('nietAftrekbareKosten') as FormArray;
        if (beperktAftrekbareKosten != undefined) {
            for (let i = 0; i < beperktAftrekbareKosten?.length; i++) {
                const group = this.fb.group<OmschrijvingEnBedrag>({
                    omschrijving: beperktAftrekbareKosten[i] ? beperktAftrekbareKosten[i].omschrijving : undefined,
                    bedrag: beperktAftrekbareKosten[i] ? beperktAftrekbareKosten[i].bedrag : undefined,
                });
                array.push(group);
            }
        }
        return array;
    }

    private createVrijgesteldeWinstbestanddelenFG(wuoVrijgesteldeWinstbestanddelen: JaarrekeningVenW[] | undefined) {
        const ondernemingFG = this.fb.group({
            wuoVrijgesteldeWinstbestanddelen: this.fb.array([]),
        });

        const array = ondernemingFG.get('wuoVrijgesteldeWinstbestanddelen') as FormArray;
        if (wuoVrijgesteldeWinstbestanddelen != undefined) {
            for (let i = 0; i < wuoVrijgesteldeWinstbestanddelen?.length; i++) {
                const group = this.fb.group<JaarrekeningVenW>({
                    omschrijving: wuoVrijgesteldeWinstbestanddelen[i] ? wuoVrijgesteldeWinstbestanddelen[i].omschrijving : undefined,
                    bedrag: wuoVrijgesteldeWinstbestanddelen[i] ? wuoVrijgesteldeWinstbestanddelen[i].bedrag : undefined,
                });
                array.push(group);
            }
        }
        return array;
    }

    private createWuoStakingsWinstenFormgroup(wuoStakingsWinsten: JaarrekeningVenW[] | undefined) {
        const ondernemingFG = this.fb.group({
            wuoStakingsWinsten: this.fb.array([]),
        });

        const array = ondernemingFG.get('wuoStakingsWinsten') as FormArray;
        if (wuoStakingsWinsten != undefined) {
            for (let i = 0; i < wuoStakingsWinsten?.length; i++) {
                const group = this.fb.group<JaarrekeningVenW>({
                    omschrijving: wuoStakingsWinsten[i] ? wuoStakingsWinsten[i].omschrijving : undefined,
                    bedrag: wuoStakingsWinsten[i] ? wuoStakingsWinsten[i].bedrag : undefined,
                });
                array.push(group);
            }
        }
        return array;
    }

    private createRestantInvesteringsaftrekFG(restantInvesteringsaftrek: OmschrijvingEnBedrag[] | undefined) {
        const ondernemingFG = this.fb.group({
            restantInvesteringsaftrek: this.fb.array([]),
        });

        const array = ondernemingFG.get('restantInvesteringsaftrek') as FormArray;
        if (restantInvesteringsaftrek != undefined) {
            for (let i = 0; i < restantInvesteringsaftrek?.length; i++) {
                const group = this.fb.group<OmschrijvingEnBedrag>({
                    omschrijving: restantInvesteringsaftrek[i] ? restantInvesteringsaftrek[i].omschrijving : undefined,
                    bedrag: restantInvesteringsaftrek[i] ? restantInvesteringsaftrek[i].bedrag : undefined,
                });
                array.push(group);
            }
        }
        return array;
    }

    private createBeginstandHIRFG(beginstandHIR: OmschrijvingEnBedrag[] | undefined) {
        const ondernemingFG = this.fb.group({
            beginstandHIR: this.fb.array([]),
        });

        const array = ondernemingFG.get('beginstandHIR') as FormArray;
        if (beginstandHIR != undefined) {
            for (let i = 0; i < beginstandHIR?.length; i++) {
                const group = this.fb.group<OmschrijvingEnBedrag>({
                    omschrijving: beginstandHIR[i] ? beginstandHIR[i].omschrijving : '',
                    bedrag: beginstandHIR[i] ? beginstandHIR[i].bedrag : 0,
                });
                array.push(group);
            }
        }
        return array;
    }

    private createBeginstandKERFG(beginstandEgalisatiereserve: OmschrijvingEnBedrag[] | undefined) {
        const ondernemingFG = this.fb.group({
            beginstandEgalisatiereserve: this.fb.array([]),
        });

        const array = ondernemingFG.get('beginstandEgalisatiereserve') as FormArray;
        if (beginstandEgalisatiereserve != undefined) {
            for (let i = 0; i < beginstandEgalisatiereserve?.length; i++) {
                const group = this.fb.group<OmschrijvingEnBedrag>({
                    omschrijving: beginstandEgalisatiereserve[i]
                        ? beginstandEgalisatiereserve[i].omschrijving
                        : (undefined as unknown as string),
                    bedrag: beginstandEgalisatiereserve[i] ? beginstandEgalisatiereserve[i].bedrag : (undefined as unknown as number),
                });
                array.push(group);
            }
        }
        return array;
    }

    private createSpecHIRFG(specificatieHerinvesteringsreserve: Herinvesteringsreserve[] | undefined) {
        const ondernemingFG = this.fb.group({
            specificatieHerinvesteringsreserve: this.fb.array([]),
        });
        const array = ondernemingFG.get('specificatieHerinvesteringsreserve') as FormArray;
        if (specificatieHerinvesteringsreserve != undefined) {
            for (let i = 0; i < specificatieHerinvesteringsreserve?.length; i++) {
                const group = this.fb.group<Herinvesteringsreserve>({
                    omschrijving: specificatieHerinvesteringsreserve[i]?.omschrijving || '',
                    jaarVanVervreemding: specificatieHerinvesteringsreserve[i]
                        ? specificatieHerinvesteringsreserve[i].jaarVanVervreemding
                        : (null as unknown as number),
                    boekwinst: specificatieHerinvesteringsreserve[i]
                        ? specificatieHerinvesteringsreserve[i].boekwinst
                        : (null as unknown as number),
                    afschrijvingspercentage: specificatieHerinvesteringsreserve[i]
                        ? specificatieHerinvesteringsreserve[i].afschrijvingspercentage
                        : (null as unknown as number),
                    boekwaardeOpVervreemdingsmoment: specificatieHerinvesteringsreserve[i]
                        ? specificatieHerinvesteringsreserve[i].boekwaardeOpVervreemdingsmoment
                        : (null as unknown as number),
                });
                array.push(group);
            }
        }
        return array;
    }

    public createResultaatSamenwerkingFormGroup(resultaatSamenwerking?: ResultaatSamenwerking): FormGroup {
        return this.fb.group({
            aandeelBelastingplichtigeInKapitaalVolgensContract: [
                resultaatSamenwerking?.aandeelBelastingplichtigeInKapitaalVolgensContract ?? 0,
            ],
            aandeelBelastingplichtigeInKapitaalOverig: [resultaatSamenwerking?.aandeelBelastingplichtigeInKapitaalOverig ?? 0],
            aandeelBelastingplichtigeInBeginVermogen: [resultaatSamenwerking?.aandeelBelastingplichtigeInBeginVermogen ?? 0],
            vergoedingVoorArbeidBelastingplichtige: [resultaatSamenwerking?.vergoedingVoorArbeidBelastingplichtige ?? 0],
            overigeVergoedingenBelastingplichtige: [resultaatSamenwerking?.overigeVergoedingenBelastingplichtige ?? 0],
            aandeelRestantWinstBelastingplichtige: [resultaatSamenwerking?.aandeelRestantWinstBelastingplichtige ?? 0],
            totaalResultaat: [{ value: 0, disabled: true }],
        });
    }

    private winstResultatenrekeningSource = new BehaviorSubject<number>(0);
    winstResultatenrekening = this.winstResultatenrekeningSource.asObservable();

    setWinstResultatenrekening(value: number): void {
        this.winstResultatenrekeningSource.next(value);
    }

    searchAnbi(
        query: (
            | { zoekWaarde: any; zoekOpdrachtType: string }
            | {
                  zoekWaarde: any;
                  zoekOpdrachtType: string;
              }
        )[],
    ): Observable<AnbiAntwoord[]> {
        return this.http.post<AnbiAntwoord[]>(this.apiAnbiUrl, query);
    }

    getKentekenGegevens(kenteken: string, privebetaald: number, beginDatum: string, eindDatum: string): Observable<Kenteken[]> {
        const url = `${this.baseUrl}/api/v1/sst/kenteken/${kenteken}/${privebetaald}/${beginDatum}/${eindDatum}`;
        return this.http.get<Kenteken[]>(url);
    }

    getCatalogusPrijs(kenteken: string): Observable<number> {
        const url = `${this.baseUrl}/api/v1/sst/catalogusprijs/${kenteken}`;
        return this.http.get<number>(url);
    }

    ngOnDestroy(): void {
        if (this.menuInterface) {
            this.menuInterface?.unsubscribe();
        }
        if (this.errorResponse) {
            this.errorResponse?.unsubscribe();
        }
        if (this.aantalValidaties) {
            this.aantalValidaties?.unsubscribe();
        }
        if (this.incomeTaxEntity) {
            this.incomeTaxEntity.unsubscribe();
        }
    }

    private fixOwr(aangifte: Aangifte): void {
        const box3 = aangifte.box3;
        if (!box3) return;

        box3.berekenWerkelijkRendement = false;

        box3.bankrekeningen?.forEach((b) => {
            b.directRendement = undefined;
            b.valutaResultaat = undefined;
            b.waardeEindJaar = undefined;
            b.stortingen = undefined;
            b.onttrekkingen = undefined;
        });

        box3.beleggingsrekeningen?.forEach((b) => {
            b.directRendement = undefined;
            b.valutaResultaat = undefined;
            b.waardeEindJaar = undefined;
            b.stortingen = undefined;
            b.onttrekkingen = undefined;
        });

        box3.groeneBeleggingen?.forEach((b) => {
            b.directRendement = undefined;
            b.valutaResultaat = undefined;
            b.waardeEindJaar = undefined;
            b.stortingen = undefined;
            b.onttrekkingen = undefined;
        });

        box3.overigeOnroerendeZaken?.forEach((b) => {
            b.directRendement = undefined;
            b.betaaldeErfpachtCanonEindeJaar = undefined;
            b.verhuurdOfVerpachtOpEinddatum = undefined;
            b.investeringen = undefined;
            b.waardestijgingWOZwaardeWegensInvesteringen = undefined;
            b.directRendement = undefined;
        });

        box3.overigeBezittingen?.forEach((b) => {
            b.directRendement = undefined;
            b.waardeEindJaar = undefined;
            b.toenames = undefined;
            b.afnames = undefined;
        });

        box3.leningen?.forEach((b) => {
            b.bijzondereWaardevermindering = undefined;
        });
    }

    public deepClean(obj: any): any {
        if (obj instanceof Date) {
            return obj;
        }
        if (Array.isArray(obj)) {
            const cleaned = obj.map(this.deepClean.bind(this)).filter((item) => {
                // Only remove if item is a non-null empty object or empty array
                if (item === undefined) return false;
                if (Array.isArray(item) && item.length === 0) return false;
                if (item && typeof item === 'object' && !(item instanceof Date) && Object.keys(item).length === 0) return false;
                return true;
            });
            return cleaned.length === 0 ? undefined : cleaned;
        } else if (obj !== null && typeof obj === 'object') {
            const newObj: any = {};
            Object.keys(obj).forEach((key) => {
                const value = this.deepClean(obj[key]);
                if (value === undefined) return;
                if (Array.isArray(value) && value.length === 0) return;
                if (
                    value !== null &&
                    typeof value === 'object' &&
                    !(value instanceof Date) &&
                    !Array.isArray(value) &&
                    Object.keys(value).length === 0
                )
                    return;
                newObj[key] = value;
            });
            return Object.keys(newObj).length === 0 ? undefined : newObj;
        }
        return obj;
    }
}
